import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { accountService } from "../../../../services/api/accountService";
import { firebaseService } from "../../../../services/api/firebaseService";
import { formatDate } from "../../../../utils/DateFormat";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import InfoItem from "../../components/InfoItem";
import Loading from "../../components/Loading";
import Modal from "../../components/Modal"; // Import Modal component
import MobilePatternHeader from "../../components/MobilePatternHeader";
import Button from "../../components/Button";

/* ================= Constants ================= */
const FALLBACK_AVATAR = "/images/avatar-default.png";

const GENDER_OPTIONS = [
  { value: "Nam", label: "Nam" },
  { value: "Nữ", label: "Nữ" },
  { value: "Khác", label: "Khác" },
];

/* ================= Helpers ================= */
const roleLabelOf = (role) => {
  switch (role) {
    case "Manager":
      return "Quản lý";
    case "Staff":
      return "Nhân viên";
    default:
      return "Cộng tác viên";
  }
};

const roleToOption = (role) => ({
  value: role,
  label: roleLabelOf(role),
});

const phoneIsValidVN = (phone) => {
  const cleaned = String(phone || "").replace(/\s+/g, "");
  return /^(0(3|5|7|8|9)\d{8}|\+84(3|5|7|8|9)\d{8})$/.test(cleaned);
};

const emailIsValid = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

const shallowChanged = (prev = {}, next = {}, keys) =>
  keys.some((k) => prev?.[k] !== next?.[k]);

/* ================= Component ================= */
const MyAccount = ({ isMobile }) => {
  const { token_client, user_client, dispatch } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Edit mode từ URL query param
  const isEditMode = searchParams.get("mode") === "edit";

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal state
  const [showCancelModal, setShowCancelModal] = useState(false);

  // View data
  const [viewData, setViewData] = useState(null);

  // Edit data
  const [editData, setEditData] = useState(null);
  const [errors, setErrors] = useState({});

  // Image handling
  const [imageSrc, setImageSrc] = useState(FALLBACK_AVATAR);
  const [imageFile, setImageFile] = useState(null);
  const [originalAvatar, setOriginalAvatar] = useState("");
  const objectUrlRef = useRef(null);

  const didFetchRef = useRef(false);
  const abortRef = useRef(null);

  const safeUser = useMemo(() => user_client || {}, [user_client]);
  const myRole = safeUser?.role || "Collaborator";
  const roleLabel = useMemo(() => roleLabelOf(myRole), [myRole]);

  /* ================= Mode Switching ================= */
  const enableEditMode = useCallback(() => {
    setSearchParams({ mode: "edit" });

    // Khởi tạo editData từ viewData
    if (viewData) {
      setEditData({
        fullName: viewData.fullName || "",
        companyName: viewData.companyName || "",
        gender: viewData.gender
          ? GENDER_OPTIONS.find((opt) => opt.value === viewData.gender)
          : null,
        email: viewData.email || "",
        phone: viewData.phone || "",
        city: viewData.city || "",
        district: viewData.district || "",
        address: viewData.address || "",
        staffCode: viewData.staffCode || "",
        username: viewData.username || "",
        password: "",
        onBoardDate: viewData.joinDate, // ISO string
        website: viewData.website || "",
        role: roleToOption(myRole),
      });
      setImageSrc(viewData.avatar || FALLBACK_AVATAR);
      setOriginalAvatar(viewData.avatar || "");
      setImageFile(null);
    }
  }, [viewData, myRole, setSearchParams]);

  const confirmCancelEdit = useCallback(() => {
    setSearchParams({});
    setErrors({});
    setImageFile(null);
    setShowCancelModal(false);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, [setSearchParams]);

  const cancelEditMode = useCallback(() => {
    setShowCancelModal(true);
  }, []);

  /* ================= Image Handling ================= */
  const handleImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Chỉ chấp nhận định dạng JPEG/PNG.");
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Kích thước tệp vượt quá 25MB.");
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    setImageSrc(url);
    setImageFile(file);
  }, []);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  /* ================= Edit Handlers ================= */
  const handleChange = useCallback((field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const handleSelectChange = useCallback((name, selectedOption) => {
    setEditData((prev) => ({ ...prev, [name]: selectedOption }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  /* ================= Validation ================= */
  const validateFields = useCallback(() => {
    const errs = {};
    const isManager = myRole === "Manager";
    const required = isManager
      ? [
          "companyName",
          "email",
          "phone",
          "city",
          "district",
          "address",
          "username",
        ]
      : [
          "fullName",
          "email",
          "phone",
          "city",
          "district",
          "address",
          "username",
        ];

    required.forEach((key) => {
      const v = editData[key];
      if (v == null || (typeof v === "string" && !v.trim())) {
        errs[key] = "Trường này không được để trống.";
      }
    });

    if (editData.email && !emailIsValid(editData.email))
      errs.email = "Email không hợp lệ.";
    if (!phoneIsValidVN(editData.phone))
      errs.phone = "Số điện thoại không hợp lệ.";
    if (editData.password && editData.password.length < 6)
      errs.password = "Mật khẩu phải lớn hơn 6 kí tự.";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [editData, myRole]);

  /* ================= Submit ================= */
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isSubmitting) return;
      if (!validateFields()) return;

      try {
        setIsSubmitting(true);
        let newAvatarUrl = null;

        // Upload avatar nếu có
        if (imageFile) {
          if (originalAvatar && originalAvatar.startsWith("https://")) {
            try {
              await firebaseService.delete_file(token_client, originalAvatar);
            } catch (delErr) {
              console.warn("Could not delete old avatar:", delErr);
            }
          }

          const uploadRes = await firebaseService.upload_file(
            token_client,
            imageFile,
          );
          if (!uploadRes?.url) {
            throw new Error("Upload avatar thất bại");
          }
          newAvatarUrl = uploadRes.url;
        }

        // Build payload
        const payload = {
          fullName: editData.fullName,
          companyName: editData.companyName,
          gender: editData.gender?.value || "",
          email: editData.email,
          phone: editData.phone,
          address: {
            city: editData.city,
            district: editData.district,
            detail: editData.address,
          },
          staffCode: editData.staffCode || "",
          username: editData.username,
          joinDate: editData.onBoardDate, // gửi ISO
          role: editData.role?.value || "",
          website: editData.website || "",
        };

        if (newAvatarUrl) {
          payload.avatarUrl = newAvatarUrl;
        }

        if (editData.password?.trim()) {
          payload.password = editData.password.trim();
        }

        await accountService.update(payload, token_client);

        toast.success("Cập nhật thông tin thành công!");

        // Tắt edit mode và refetch
        setSearchParams({});
        didFetchRef.current = false;
        fetchUserData();
      } catch (error) {
        console.error("Error updating user:", error);
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Không thể kết nối tới server. Vui lòng thử lại.",
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      validateFields,
      imageFile,
      originalAvatar,
      editData,
      token_client,
      setSearchParams,
    ],
  );

  /* ================= Fetch User Data ================= */
  const fetchUserData = useCallback(async () => {
    if (!token_client) return;

    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      const resp = await accountService.get_me(token_client, controller.signal);

      const address = resp.address || {};
      const formatted = {
        avatar: resp.avatar || resp.avatarUrl || FALLBACK_AVATAR,
        fullName: resp.fullName || "--",
        companyName: resp.companyName || "--",
        dob: resp.dateOfBirth ? formatDate(resp.dateOfBirth) : "--",
        gender: resp.gender || "--",
        email: resp.email || "--",
        phone: resp.phone || "--",
        city: address.city || "--",
        district: address.district || "--",
        address: address.detail || "--",
        staffCode: resp.staffCode || "--",
        username: resp.username || "--",
        joinDate: resp.joinDate || "", // ISO
        joinDateDisplay: resp.joinDate ? formatDate(resp.joinDate) : "--",
        status: resp.status || "--",
        website: resp.website || "",
      };

      setViewData(formatted);
      setImageSrc(formatted.avatar);

      // Update context nếu cần
      const user_data = {
        ...safeUser,
        MST: resp?.MST ?? safeUser?.MST,
        avatar: resp?.avatar ?? resp?.avatarUrl ?? safeUser?.avatar,
        fullName: resp?.fullName ?? safeUser?.fullName,
        companyName: resp?.companyName ?? safeUser?.companyName,
        username: resp?.username ?? safeUser?.username,
        phone: resp?.phone ?? safeUser?.phone,
        email: resp?.email ?? safeUser?.email,
      };

      const keys = Object.keys(user_data);
      if (shallowChanged(safeUser, user_data, keys)) {
        dispatch({
          type: "UPDATE_USER_INFO",
          payload: { user_client: user_data },
        });
      }
    } catch (error) {
      if (error?.name === "AbortError") return;
      toast.error(
        error?.response?.data?.message ||
          "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
    } finally {
      setLoading(false);
    }
  }, [token_client, dispatch, safeUser]);

  useEffect(() => {
    if (!token_client) return;
    if (didFetchRef.current) return;
    didFetchRef.current = true;
    fetchUserData();
    return () => abortRef.current?.abort?.();
  }, [token_client, fetchUserData]);

  /* ================= Computed Values ================= */
  const displayName = useMemo(
    () =>
      safeUser.username || safeUser.fullName || safeUser.companyName || "--",
    [safeUser.username, safeUser.fullName, safeUser.companyName],
  );

  const addressLine = useMemo(() => {
    if (!viewData) return "--";
    return [
      viewData.address || "--",
      viewData.district || "--",
      viewData.city || "--",
    ].join(", ");
  }, [viewData]);

  /* ================= Render Loading ================= */
  if (loading) {
    return (
      <div
        className={`flex ${isMobile ? "min-h-[100dvh] w-full" : "h-full"} items-center justify-center`}
      >
        <Loading />
      </div>
    );
  }

  if (!viewData) {
    return (
      <div className="flex min-h-[100dvh] w-full items-center justify-center">
        <p>Đang tải dữ liệu...</p>
      </div>
    );
  }

  /* ================= MOBILE VIEW ================= */
  if (isMobile) {
    if (isEditMode) {
      // Mobile Edit Mode
      return (
        <div className="h-full w-full">
          {isSubmitting && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <Loading />
            </div>
          )}

          {/* Cancel Confirmation Modal */}
          <Modal
            isOpen={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            title="Xác nhận hủy"
            maxWidth="420px"
            showCancelButton
            cancelButtonText="Tiếp tục chỉnh sửa"
            primaryButtonText="Hủy chỉnh sửa"
            primaryButtonVariant="danger"
            primaryButtonOnClick={confirmCancelEdit}
          >
            <div className="px-6 py-4">
              <p className="text-gray-700">
                Bạn có chắc chắn muốn hủy chỉnh sửa không?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Các thay đổi chưa lưu sẽ bị mất.
              </p>
            </div>
          </Modal>

          <MobilePatternHeader
            title="Chỉnh sửa tài khoản"
            onBack={cancelEditMode}
          />

          <div className="h-[calc(100%-64px)] bg-[#F2F2F2] p-3">
            <form
              className="h-[calc(100%-70px)] w-full overflow-auto"
              onSubmit={handleSubmit}
            >
              <div className="w-full flex flex-col items-center justify-center mt-1">
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer size-[100px] rounded-full bg-white flex items-center justify-center overflow-hidden relative hover:opacity-60"
                >
                  <img
                    src={imageSrc}
                    alt="Avatar"
                    className="size-[100px] object-cover rounded-full"
                  />
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/jpeg,image/png"
                    className="hidden"
                    onChange={handleImageChange}
                    disabled={isSubmitting}
                  />
                </label>
                <p className="text-[14px] font-sf-medium text-[#0267FF] mt-1">
                  Tải ảnh lên
                </p>
              </div>

              <div className="space-y-3">
                <p className="text-[14px] font-sf-medium text-[#0267FF] pb-1 border-b border-[#E1E5F3]">
                  Thông tin chung
                </p>

                <CustomInput
                  placeholder="Tên công ty / Tổ chức"
                  value={editData?.companyName}
                  required={myRole === "Manager"}
                  onChange={(v) => handleChange("companyName", v)}
                  error={errors.companyName}
                />
                <CustomInput
                  placeholder="Nhập họ tên"
                  value={editData?.fullName}
                  required={myRole !== "Manager"}
                  onChange={(v) => handleChange("fullName", v)}
                  error={errors.fullName}
                />

                <div className="grid grid-cols-2 gap-3">
                  <CustomInput
                    placeholder="Ngày tham gia"
                    value={formatDate(editData?.onBoardDate)}
                    disabled
                  />

                  <CustomSelect
                    height="52px"
                    required
                    options={GENDER_OPTIONS}
                    placeholder="Giới tính"
                    value={editData?.gender}
                    onChange={(opt) => handleSelectChange("gender", opt)}
                    className="bg-white"
                    error={errors.gender}
                  />
                </div>

                <CustomInput
                  placeholder="Số điện thoại"
                  value={editData?.phone}
                  required
                  error={errors.phone}
                  onChange={(v) => handleChange("phone", v)}
                />
                <CustomInput
                  placeholder="Email"
                  type="email"
                  value={editData?.email}
                  required
                  error={errors.email}
                  onChange={(v) => handleChange("email", v)}
                />

                <div className="grid grid-cols-2 gap-3">
                  <CustomInput
                    placeholder="Tỉnh/Thành phố"
                    value={editData?.city}
                    required
                    error={errors.city}
                    onChange={(v) => handleChange("city", v)}
                  />
                  <CustomInput
                    placeholder="Quận/Huyện"
                    value={editData?.district}
                    required
                    error={errors.district}
                    onChange={(v) => handleChange("district", v)}
                  />
                </div>
                <CustomInput
                  placeholder="Địa chỉ"
                  value={editData?.address}
                  required
                  error={errors.address}
                  onChange={(v) => handleChange("address", v)}
                />
              </div>

              <div className="space-y-3 mt-3">
                <p className="text-[14px] font-sf-medium text-[#0267FF] pb-1 border-b border-[#E1E5F3]">
                  Thông tin tài khoản
                </p>
                <CustomInput
                  placeholder="Tên tài khoản"
                  value={editData?.username}
                  required
                  error={errors.username}
                  onChange={(v) => handleChange("username", v)}
                />

                <div className="grid grid-cols-2 gap-3">
                  <CustomInput
                    placeholder="Mã số nhân viên"
                    value={editData?.staffCode}
                    disabled
                  />
                  <CustomInput
                    placeholder="Mật khẩu mới"
                    type="password"
                    value={editData?.password}
                    error={errors.password}
                    onChange={(v) => handleChange("password", v)}
                  />
                </div>

                <CustomInput value={roleLabel} disabled />
              </div>

              <div className="flex justify-end my-6 gap-4 w-full">
                <Button
                  type="button"
                  onClick={cancelEditMode}
                  disabled={isSubmitting}
                  variant="secondary"
                  size="lg"
                  className="w-full"
                >
                  Hủy
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  isLoading={isSubmitting}
                  variant="primary"
                  size="lg"
                  className="w-full"
                >
                  {isSubmitting ? "Đang lưu..." : "Lưu"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      );
    }

    // Mobile View Mode
    return (
      <div className="w-full min-h-[100dvh] pt-[90px] relative">
        <div className="w-full h-[calc(100%-0px)] bg-white rounded-2xl p-3">
          <div className="flex items-center justify-center flex-col">
            <h1 className="mt-8 align-middle text-[#0267FF] text-[20px] font-sf-semibold">
              {displayName}
            </h1>
            <p className="opacity-70">{roleLabel}</p>
          </div>

          <div className="overflow-auto h-[calc(100%-220px)] space-y-4">
            <div className="space-y-2">
              <p className="font-sf-medium">Thông tin chung</p>
              <div className="border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p>Ngày sinh:</p>
                  <p>{viewData.dob}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Giới tính:</p>
                  <p>{viewData.gender}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Số điện thoại:</p>
                  <p>{viewData.phone}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Email:</p>
                  <p>{viewData.email}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Địa chỉ:</p>
                  <p className="text-right">{addressLine}</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <p className="font-sf-medium">Thông tin tài khoản</p>
              <div className="border shadow-xl rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p>Mã số nhân viên:</p>
                  <p>{viewData.staffCode}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Tên tài khoản:</p>
                  <p>{viewData.username}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Mật khẩu:</p>
                  <p>*******</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Phân quyền:</p>
                  <p>{roleLabel}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute top-[30px] w-full flex items-center justify-center">
          <img
            src={safeUser.avatar || FALLBACK_AVATAR}
            alt="User avatar"
            width={100}
            height={100}
            className="rounded-full object-cover"
            style={{ width: 100, height: 100 }}
          />
        </div>

        <div className="w-full p-3 absolute bottom-[85px] left-0">
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={enableEditMode}
            className="w-full"
          >
            Chỉnh sửa
          </Button>
        </div>
      </div>
    );
  }

  /* ================= DESKTOP VIEW ================= */
  if (isEditMode) {
    // Desktop Edit Mode
    return (
      <div className="h-full overflow-auto">
        {isSubmitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Loading />
          </div>
        )}

        {/* Cancel Confirmation Modal */}
        <Modal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          title="Xác nhận hủy"
          maxWidth="420px"
          showCancelButton
          cancelButtonText="Tiếp tục chỉnh sửa"
          primaryButtonText="Hủy chỉnh sửa"
          primaryButtonVariant="danger"
          primaryButtonOnClick={confirmCancelEdit}
        >
          <div className="px-6 py-4">
            <p className="text-gray-700">
              Bạn có chắc chắn muốn hủy chỉnh sửa không?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Các thay đổi chưa lưu sẽ bị mất.
            </p>
          </div>
        </Modal>

        <div className="bg-white rounded-lg p-6">
          <div className="flex items-center mb-6 w-full border-b px-[15vw] py-4">
            <img src="/icons/user.svg" alt="user icon" />
            <h2>Thông tin chung</h2>
          </div>

          <form className="space-y-6 px-[15vw]" onSubmit={handleSubmit}>
            <div className="flex justify-between items-start border-b">
              <div className="flex items-center space-x-4 pb-6">
                <div className="relative w-[84px] h-[84px] cursor-pointer group">
                  <img
                    src={imageSrc}
                    alt="Avatar"
                    className="w-full h-full rounded-lg object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black bg-opacity-40 rounded-lg">
                    <img
                      src="/icons/camera-icon.svg"
                      alt="Camera"
                      className="w-8 h-8"
                    />
                  </div>
                  <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept="image/jpeg,image/png"
                    onChange={handleImageChange}
                    disabled={isSubmitting}
                  />
                </div>

                {myRole === "Manager" && (
                  <div className="flex flex-col gap-1 py-1">
                    <strong className="text-gray-700 text-sm">Website</strong>
                    <CustomInput
                      placeholder="Website"
                      value={editData?.website}
                      onChange={(v) => handleChange("website", v)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {myRole === "Manager" ? (
                <div className="flex flex-col gap-1 py-1">
                  <strong className="text-gray-700 text-sm">
                    Tên công ty / Tổ chức
                  </strong>
                  <CustomInput
                    placeholder="Tên công ty / Tổ chức"
                    value={editData?.companyName}
                    required
                    onChange={(v) => handleChange("companyName", v)}
                    error={errors.companyName}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-1 py-1">
                  <strong className="text-gray-700 text-sm">Họ và tên</strong>
                  <CustomInput
                    placeholder="Họ và tên"
                    value={editData?.fullName}
                    required
                    onChange={(v) => handleChange("fullName", v)}
                    error={errors.fullName}
                  />
                </div>
              )}

              <div className="flex gap-4">
                <div className="flex flex-col gap-1 py-1 w-full">
                  <strong className="text-gray-700 text-sm">
                    {myRole === "Manager" ? "Ngày tạo" : "Ngày tham gia"}
                  </strong>
                  <CustomInput
                    placeholder="Ngày tham gia"
                    value={formatDate(editData?.onBoardDate)}
                    disabled
                    required
                  />
                </div>
                <div className="flex flex-col gap-1 py-1 w-full">
                  <strong className="text-gray-700 text-sm">Giới tính</strong>
                  <CustomSelect
                    height="52px"
                    options={GENDER_OPTIONS}
                    placeholder="Giới tính"
                    value={editData?.gender}
                    onChange={(opt) => handleSelectChange("gender", opt)}
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1 py-1">
                <strong className="text-gray-700 text-sm">Email</strong>
                <CustomInput
                  placeholder="Email"
                  type="email"
                  value={editData?.email}
                  required
                  onChange={(v) => handleChange("email", v)}
                  error={errors.email}
                />
              </div>

              <div className="flex flex-col gap-1 py-1">
                <strong className="text-gray-700 text-sm">Số điện thoại</strong>
                <CustomInput
                  placeholder="Số điện thoại"
                  value={editData?.phone}
                  required
                  onChange={(v) => handleChange("phone", v)}
                  error={errors.phone}
                />
              </div>

              <div className="flex flex-col gap-1 py-1">
                <strong className="text-gray-700 text-sm">
                  Tỉnh / Thành phố
                </strong>
                <CustomInput
                  placeholder="Tỉnh / Thành phố"
                  value={editData?.city}
                  onChange={(v) => handleChange("city", v)}
                  required
                  error={errors.city}
                />
              </div>

              <div className="flex flex-col gap-1 py-1">
                <strong className="text-gray-700 text-sm">Quận / Huyện</strong>
                <CustomInput
                  placeholder="Quận / Huyện"
                  value={editData?.district}
                  onChange={(v) => handleChange("district", v)}
                  required
                  error={errors.district}
                />
              </div>

              <div className="flex flex-col gap-1 py-1 col-span-2">
                <strong className="text-gray-700 text-sm">Địa chỉ</strong>
                <CustomInput
                  placeholder="Địa chỉ"
                  value={editData?.address}
                  onChange={(v) => handleChange("address", v)}
                  required
                  error={errors.address}
                />
              </div>
            </div>

            <div>
              <div className="flex gap-2 mb-4">
                <img src="/icons/bag.svg" alt="Bag icon" />
                Thông tin tài khoản
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="flex flex-col gap-1 py-1">
                  <strong className="text-gray-700">Mã số nhân viên</strong>
                  <CustomInput
                    placeholder="Mã số nhân viên"
                    value={editData?.staffCode}
                    disabled
                  />
                </div>

                <div className="flex flex-col gap-1 py-1">
                  <strong className="text-gray-700">Tên tài khoản</strong>
                  <CustomInput
                    placeholder="Tên tài khoản"
                    value={editData?.username}
                    onChange={(v) => handleChange("username", v)}
                    required
                    error={errors.username}
                  />
                </div>

                <div className="flex flex-col gap-1 py-1">
                  <strong className="text-gray-700">Mật khẩu</strong>
                  <CustomInput
                    type="password"
                    placeholder="Nhập mật khẩu mới"
                    value={editData?.password}
                    onChange={(v) => handleChange("password", v)}
                    error={errors.password}
                  />
                </div>

                <div className="flex flex-col gap-1 py-1 col-span-3">
                  <strong className="text-gray-700">Phân quyền</strong>
                  <CustomInput value={roleLabel} disabled />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="secondary"
                size="lg"
                onClick={cancelEditMode}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                isLoading={isSubmitting}
                variant="primary"
                size="lg"
              >
                {isSubmitting ? "Đang lưu..." : "Chỉnh sửa"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Desktop View Mode
  return (
    <div className="h-full overflow-auto">
      <div className="bg-white rounded-lg p-6">
        <div className="flex items-center mb-6 w-full border-b px-[15vw] py-4">
          <img src="/icons/user.svg" alt="user icon" />
          <h2>Thông tin chung</h2>
        </div>

        <div className="space-y-6 px-[15vw]">
          <div className="flex justify-between items-center border-b">
            <div className="flex items-center space-x-6 pb-6">
              <img
                src={viewData.avatar}
                alt="Avatar"
                width={94}
                height={94}
                className="rounded-lg object-cover"
                style={{ width: 94, height: 94 }}
              />
              <div className="space-y-3">
                <h3 className="text-xl font-semibold">{viewData.fullName}</h3>
                <div className="text-gray-600 flex items-center gap-2">
                  <img src="/icons/mail.svg" alt="Mail icon" />
                  {viewData.email}
                </div>
                <div className="flex items-center gap-2">
                  <img src="/icons/link.svg" alt="Hyperlink icon" />
                  {viewData.website ? (
                    <a
                      href={viewData.website}
                      className="text-blue-500 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {viewData.website}
                    </a>
                  ) : (
                    <span className="text-gray-500">-- website</span>
                  )}
                </div>
              </div>
            </div>

            <Button
              type="button"
              variant="primary"
              size="lg"
              onClick={enableEditMode}
              leftIcon={<img src="/icons/edit.svg" alt="" />}
            >
              Chỉnh sửa
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {safeUser.role === "Manager" ? (
              <InfoItem
                label="Tên công ty / Tổ chức"
                value={viewData.companyName}
              />
            ) : (
              <InfoItem label="Họ và tên" value={viewData.fullName} />
            )}

            <div className="flex gap-4 w-full">
              <InfoItem
                label={
                  safeUser.role === "Manager" ? "Ngày tạo" : "Ngày tham gia"
                }
                value={formatDate(viewData.joinDate)}
                className="w-1/2"
              />
              <InfoItem
                label="Giới tính"
                value={viewData.gender}
                className="w-1/2"
              />
            </div>

            <InfoItem label="Email" value={viewData.email} />
            <InfoItem label="Số điện thoại" value={viewData.phone} />
            <InfoItem label="Tỉnh / Thành phố" value={viewData.city} />
            <InfoItem label="Quận / Huyện" value={viewData.district} />
            <InfoItem
              label="Địa chỉ"
              value={viewData.address}
              className="col-span-2"
            />
          </div>

          <div>
            <div className="flex gap-2 mb-4">
              <img src="/icons/bag.svg" alt="Bag icon" />
              Thông tin tài khoản
            </div>

            <div className="grid grid-cols-3 gap-4">
              <InfoItem label="Mã số nhân viên" value={viewData.staffCode} />
              <InfoItem label="Tên tài khoản" value={viewData.username} />
              <InfoItem label="Mật khẩu" value="********" />
              <InfoItem
                label="Phân quyền"
                value={roleLabel}
                className="col-span-3"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
